import { useCallback } from 'react';

import { randomUUID } from 'expo-crypto';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { calculateCartSubTotal } from 'features/cart/utils';
import { findRewardBenefitFromHydratedCartEntry } from 'features/cart/utils/minimal-cart';
import { useStoreContext } from 'features/store';
import { omit } from 'lodash-es';

import { ILegacyCartEntry, IOfferDiscount } from '@rbi-ctg/menu';
import { OfferDiscountTypes } from 'enums/menu';
import { useHydrateCartMutation } from 'generated/graphql-gateway';
import { useGetComboLazyQuery } from 'generated/sanity-graphql';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { validateAppliedReward } from 'state/global-state/models/loyalty/rewards/rewards.slice';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useServiceModeContext } from 'state/service-mode';
import { computeTotalWithOfferDiscount } from 'utils/cart/helper';
import { blockContentToPlainText, emptySanityImage } from 'utils/sanity';

import { hydrateAndPersistEntries, hydrateEntries } from '../../../hooks/hydrateEntries';
import { useCart } from '../../../hooks/use-cart';

interface IHydrateMinimalCartOptions {
  storeId: string;
  fetchSanityData?: boolean;
}

type TRewardMinimalCartEntry = ICartEntry & Required<Pick<ICartEntry, 'rewardId' | 'cartId'>>;

/**
 * Expose the cart state and the utility methods to mutate it.
 *
 * When an action to mutate the cart is triggered, it manages the Redux cart actions, logs the events,
 * coordinates with other parts of the code, and dispatches actions on related slices.
 */
export const useMinimalCart = () => {
  const dispatch = useAppDispatch();
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );
  const totalTimesRewardApplied = useAppSelector(selectors.loyalty.selectTotalTimesRewardApplied);
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);
  const cmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);

  const { store } = useStoreContext();
  const storeId = store?.number;
  const { serviceMode } = useServiceModeContext();
  const [hydrateCart] = useHydrateCartMutation();
  const { addItemToCart, updateCartEntry } = useCart();
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const enableMinimalCartUi = useFlag(LaunchDarklyFlag.ENABLE_MINIMAL_CART_UI);
  const appliedLoyaltyDiscountOffer = useAppSelector(
    selectors.loyalty.selectDiscountAppliedCmsOffer
  );

  const enableCartHydrationOnStoreChange = useFlag(
    LaunchDarklyFlag.ENABLE_CART_HYDRATION_ON_STORE_CHANGE
  );

  const [fetchCombo] = useGetComboLazyQuery();

  const addDiscountOfferToMinimalCart = useCallback(
    discountOffer => {
      const subTotalWithoutDiscount = calculateCartSubTotal(minimalCartEntries);
      const incentive = discountOffer.incentives[0] as IOfferDiscount;
      const discountValue =
        computeTotalWithOfferDiscount(subTotalWithoutDiscount, incentive) - subTotalWithoutDiscount;

      dispatch(
        actions.ordering.addMinimalCartEntry({
          children: [],
          id: discountOffer._id,
          cartId: DISCOUNT_OFFER_CART_ID,
          image: emptySanityImage(),
          name: blockContentToPlainText(discountOffer.name?.localeRaw),
          type: MinimalCartEntryType.discount,
          quantity: 1,
          price: {
            amount: discountValue,
            currency: 'USD',
          },
          url: '',
        })
      );
    },
    [dispatch, minimalCartEntries]
  );

  const handleDiscountOfferUpdate = useCallback(() => {
    const discountOffer = minimalCartEntries.find(
      entry => entry.type === MinimalCartEntryType.discount
    );

    if (!appliedLoyaltyDiscountOffer || !discountOffer) {
      return;
    }

    const incentive = appliedLoyaltyDiscountOffer.incentives[0] as IOfferDiscount;
    if (discountOffer && incentive.discountType === OfferDiscountTypes.PERCENTAGE) {
      dispatch(actions.ordering.removeMinimalCartEntry(discountOffer));
      addDiscountOfferToMinimalCart(appliedLoyaltyDiscountOffer);
    }
  }, [dispatch, addDiscountOfferToMinimalCart, minimalCartEntries, appliedLoyaltyDiscountOffer]);

  // Adds a single cartEntry to the cart
  const addItemToMinimalCart = useCallback(
    (minimalCartEntry: ICartEntry) => {
      /**
       * When attempting to add several items with applied reward and reward unstacking is enabled,
       * add one entry line for the reward and a separate line for the other items (without the reward)
       */
      if (enableRewardUnstacking && minimalCartEntry.rewardId && minimalCartEntry.quantity > 1) {
        dispatch(actions.ordering.addMinimalCartEntry({ ...minimalCartEntry, quantity: 1 }));
        dispatch(
          actions.ordering.addMinimalCartEntry({
            ...omit(minimalCartEntry, ['rewardId', 'rewardBenefitId', 'cartId']),
            type: MinimalCartEntryType.item,
            cartId: randomUUID(),
            quantity: minimalCartEntry.quantity - 1,
          })
        );
      } else {
        dispatch(actions.ordering.addMinimalCartEntry(minimalCartEntry));
        handleDiscountOfferUpdate();
      }
    },
    [dispatch, enableRewardUnstacking, handleDiscountOfferUpdate]
  );

  const emptyMinimalCart = useCallback(() => {
    dispatch(actions.ordering.resetMinimalCart());
    dispatch(actions.loyalty.setSelectedOffer(null));
    dispatch(actions.loyalty.resetAppliedOffers());
    dispatch(actions.loyalty.resetAppliedDiscountOffers());
    dispatch(
      actions.loyalty.removeAppliedRewards({
        cartIds: minimalCartEntries.map(entry => entry.cartId),
      })
    );
  }, [dispatch, minimalCartEntries]);

  const removeItemFromMinimalCart = useCallback(
    (cartEntry: ICartEntry) => {
      dispatch(actions.ordering.removeMinimalCartEntry(cartEntry));
      if (
        cartEntry.type !== MinimalCartEntryType.discount &&
        minimalCartEntries.length > 0 &&
        enableMinimalCartUi
      ) {
        handleDiscountOfferUpdate();
      }
    },
    [dispatch, handleDiscountOfferUpdate, minimalCartEntries.length, enableMinimalCartUi]
  );

  const handleRewards = useCallback(
    (rewardBenefitId: string, cartEntry: ILegacyCartEntry, remainingPoints: { value: number }) => {
      let cartEntryWithReward = { ...cartEntry };

      const { isApplicable, mappedReward } = validateAppliedReward(
        availableLoyaltyRewardsMap,
        rewardBenefitId,
        totalTimesRewardApplied,
        1,
        remainingPoints.value
      );

      if (isApplicable) {
        dispatch(
          actions.loyalty.applyReward({
            rewardBenefitId,
            cartId: cartEntryWithReward.cartId,
            quantity: 1,
            redemptionMethod: RewardRedemptionMethod.MOBILE,
            enableRewardUnstacking,
          })
        );
        remainingPoints.value -= mappedReward?.pointCost ?? 0;
      } else {
        const rewardBenefit = findRewardBenefitFromHydratedCartEntry(cartEntry);
        if (rewardBenefit) {
          cartEntryWithReward = { ...rewardBenefit };
          const newId = randomUUID();
          cartEntryWithReward.cartId = newId;
          // @ts-ignore
          cartEntryWithReward.lineId = newId;
        }
      }
      return cartEntryWithReward;
    },
    [availableLoyaltyRewardsMap, dispatch, totalTimesRewardApplied, enableRewardUnstacking]
  );

  const hydrateMinimalCart = useCallback(
    async (entries?: ICartEntry[], options?: IHydrateMinimalCartOptions) => {
      if (!storeId || !serviceMode) {
        return false;
      }

      // This is a variation for the hydrateEntries function for cart hydration when a store changes.
      // This will hydrate the cart entries and will fill them with the missing sanity information.
      // This missing information for the cart entries is used for repricing.
      if (
        enableCartHydrationOnStoreChange &&
        !!entries?.length &&
        !!options?.storeId &&
        !!options?.fetchSanityData
      ) {
        return hydrateAndPersistEntries(
          entries,
          options.storeId,
          fetchCombo,
          serviceMode,
          hydrateCart,
          addItemToCart,
          updateCartEntry,
          dispatch,
          cmsOffers,
          handleRewards,
          stagedCartPoints
        );
      }

      return hydrateEntries(
        entries ?? minimalCartEntries,
        options?.storeId || storeId,
        serviceMode,
        hydrateCart,
        addItemToCart,
        updateCartEntry,
        dispatch,
        handleRewards,
        stagedCartPoints
      );
    },
    [
      addItemToCart,
      cmsOffers,
      dispatch,
      enableCartHydrationOnStoreChange,
      fetchCombo,
      handleRewards,
      hydrateCart,
      minimalCartEntries,
      serviceMode,
      stagedCartPoints,
      storeId,
      updateCartEntry,
    ]
  );

  const getAppliedReward = useCallback(() => {
    return minimalCartEntries.find(
      (entry): entry is TRewardMinimalCartEntry => !!entry.rewardId && !!entry.cartId
    );
  }, [minimalCartEntries]);

  return {
    cartEntries: minimalCartEntries,
    addItemToMinimalCart,
    addDiscountOfferToMinimalCart,
    removeItemFromMinimalCart,
    emptyMinimalCart,
    hydrateMinimalCart,
    getAppliedReward,
  };
};
